import { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { Btn } from '../../../AbstractElements';
import LegalConsultationService from '../../../Api/services/legal-consultation.service';
import type { components } from '../../../Api/types/openapi';
import { useTranslation } from 'react-i18next';

type LegalConsulationResponse = components['schemas']['LegalConsulationResponse'];
type LegalConsulationForClientResponse = components['schemas']['LegalConsulationForClientResponse'];
type LegalConsulationForLawyerResponse = components['schemas']['LegalConsulationForLawyerResponse'];
type RescheduleLegalConsulation = components['schemas']['RescheduleLegalConsulation'];

type ConsultationData = LegalConsulationResponse | LegalConsulationForClientResponse | LegalConsulationForLawyerResponse;

interface RescheduleConsultationModalProps {
  isOpen: boolean;
  toggle: () => void;
  consultation: ConsultationData | null;
  onSuccess: () => void;
}

const RescheduleConsultationModal: React.FC<RescheduleConsultationModalProps> = ({
  isOpen,
  toggle,
  consultation,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<RescheduleLegalConsulation>({
    id: null,
    time_zone: '',
    consultation_date: '',
    init_consultation_time: '',
    end_consultation_time: '',
    description: null,
  });

  useEffect(() => {
    if (isOpen && consultation) {
      setFormData({
        id: consultation.id || null,
        time_zone: consultation.time_zone || '',
        consultation_date: consultation.consultation_date || '',
        init_consultation_time: consultation.init_consultation_time || '',
        end_consultation_time: consultation.end_consultation_time || '',
        description: consultation.description || null,
      });
    }
  }, [isOpen, consultation]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.consultation_date || !formData.init_consultation_time || !formData.end_consultation_time) {
      toast.error(t('La fecha y hora son requeridas'));
      return;
    }

    if (!formData.id) {
      toast.error(t('ID de consulta no encontrado'));
      return;
    }

    try {
      setLoading(true);
      await LegalConsultationService.rescheduleConsultation(formData);
      toast.success(t('Consulta reagendada exitosamente'));
      toggle();
      onSuccess();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || t('Error al reagendar consulta');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>{t('Reagendar Consulta')}</ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <FormGroup>
            <Label>{t('Zona Horaria')} *</Label>
            <Input
              type="text"
              value={formData.time_zone}
              onChange={(e) => setFormData({ ...formData, time_zone: e.target.value })}
              placeholder="Ej: America/New_York"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>{t('Fecha de Consulta')} *</Label>
            <Input
              type="date"
              value={formData.consultation_date}
              onChange={(e) => setFormData({ ...formData, consultation_date: e.target.value })}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>{t('Hora de Inicio')} *</Label>
            <Input
              type="time"
              value={formData.init_consultation_time}
              onChange={(e) => setFormData({ ...formData, init_consultation_time: e.target.value })}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>{t('Hora de Fin')} *</Label>
            <Input
              type="time"
              value={formData.end_consultation_time}
              onChange={(e) => setFormData({ ...formData, end_consultation_time: e.target.value })}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>{t('Descripción')}</Label>
            <Input
              type="textarea"
              rows={4}
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value || null })}
              placeholder={t('Descripción de la consulta (opcional)')}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Btn color="secondary" onClick={toggle} disabled={loading}>
            {t('Cancelar')}
          </Btn>
          <Btn color="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                {t('Reagendando...')}
              </>
            ) : (
              t('Reagendar')
            )}
          </Btn>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default RescheduleConsultationModal;

